import {
  type UserMessagePreviewChannel,
  type UserMessagePreviewTemplateId,
  type UserMessagePreviewVariant,
} from '@aikoa/common'
import { useCurrentLanguage, useTranslations } from '@aikoa/i18n/react'
import { useMemo, useState } from 'react'
import { View } from 'react-native'
import ContentBox from '~/components/layout/ContentBox'
import { SectionTitle } from '~/components/layout/Sections'
import { LoadingView } from '~/components/LoadingView'
import {
  getMessagePreviewChannelLabel,
  getMessagePreviewTemplateDescription,
  getMessagePreviewTemplateTitle,
  getMessagePreviewVariantLabel,
} from '~/components/messages/messagePreviewCopy'
import { MessagePreviewPanel } from '~/components/messages/MessagePreviewPanel'
import { Body, DisplayValue } from '~/components/typography/Content'
import { TabBar, TabContent, Tabs, type TabItem } from '~/components/ui/tabs'
import { Text } from '~/components/ui/text'
import { useUserMessagePreviews } from '~/data/user/useUserMessagePreviews'
import { cn } from '~/lib/utils'

type PreviewData = {
  channel: UserMessagePreviewChannel
  variant: UserMessagePreviewVariant
  subject: string | null
  html: string | null
  text: string
}

function TemplatePreviewGroup({ previews }: { previews: PreviewData[] }) {
  const { t } = useTranslations('user')

  const hasMultipleChannels = useMemo(
    () => new Set(previews.map((preview) => preview.channel)).size > 1,
    [previews]
  )

  return (
    <View className="gap-6 rounded-content-inner bg-secondary/20 px-4 py-5">
      {previews.map((preview) => {
        const label = hasMultipleChannels
          ? getMessagePreviewChannelLabel(t, preview.channel)
          : getMessagePreviewVariantLabel(t, preview.variant)

        return (
          <View key={`${preview.channel}:${preview.variant}`} className="gap-2">
            <Text className="font-medium text-xs uppercase tracking-widest text-secondary">
              {label}
            </Text>
            <MessagePreviewPanel preview={preview} />
          </View>
        )
      })}
    </View>
  )
}

export function MessagePreviewsView() {
  const { t } = useTranslations('user')
  const currentLanguage = useCurrentLanguage()
  const { data: previews, isLoading, isError } = useUserMessagePreviews(currentLanguage)

  const templateIds = useMemo(() => {
    const uniqueIds: UserMessagePreviewTemplateId[] = []

    for (const preview of previews ?? []) {
      if (!uniqueIds.includes(preview.templateId)) {
        uniqueIds.push(preview.templateId)
      }
    }

    return uniqueIds
  }, [previews])

  const [selectedTemplateId, setSelectedTemplateId] =
    useState<UserMessagePreviewTemplateId>('feedbackRequest')

  if (templateIds.length > 0 && !templateIds.includes(selectedTemplateId)) {
    setSelectedTemplateId(templateIds[0])
  }

  if (isLoading) {
    return <LoadingView />
  }

  if (isError || !previews) {
    return (
      <View className="gap-3">
        <SectionTitle>{t(($) => $.messagePreviews.title)}</SectionTitle>
        <Body className="text-center text-secondary">
          {t(($) => $.messagePreviews.loadingError)}
        </Body>
      </View>
    )
  }

  if (templateIds.length === 0) {
    return (
      <View className="gap-3">
        <SectionTitle>{t(($) => $.messagePreviews.title)}</SectionTitle>
        <Body className="text-center text-secondary">{t(($) => $.messagePreviews.empty)}</Body>
      </View>
    )
  }

  const templateTabs = templateIds.map((templateId, index) => ({
    id: templateId,
    label: getMessagePreviewTemplateTitle(t, templateId),
    content: (
      <ContentBox
        scrollable={false}
        contentContainerClassName="gap-4"
        className={cn(
          'gap-6',
          index === 0 && 'rounded-tl-none rounded-tr-content',
          index === templateIds.length - 1 && 'rounded-tr-none'
        )}>
        <DisplayValue className="text-secondary">
          {getMessagePreviewTemplateTitle(t, templateId)}
        </DisplayValue>
        <Body>{getMessagePreviewTemplateDescription(t, templateId)}</Body>

        <TemplatePreviewGroup
          previews={previews.filter((preview) => preview.templateId === templateId)}
        />
      </ContentBox>
    ),
  })) satisfies TabItem<UserMessagePreviewTemplateId>[]

  return (
    <View className="gap-3">
      <ContentBox scrollable={false} contentContainerClassName="gap-3">
        <SectionTitle>{t(($) => $.messagePreviews.title)}</SectionTitle>

        <Body>{t(($) => $.messagePreviews.description)}</Body>

        <Body>{t(($) => $.messagePreviews.exampleNote)}</Body>
      </ContentBox>

      <View>
        <Tabs
          tabs={templateTabs}
          activeTab={selectedTemplateId}
          setActiveTab={setSelectedTemplateId}>
          <TabBar tabButtonTextClassName="font-normal text-sm/relaxed sm:text-base/relaxed" />
          <TabContent />
        </Tabs>
      </View>
    </View>
  )
}
